import { alpha } from "@mui/material/styles";
import { Box, Button, Collapse, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

export interface ErrorFallbackProps {
  errorMessage?: string;
  componentStack?: string;
  onRetry?: () => void;
  isPreview?: boolean;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  errorMessage,
  componentStack,
  onRetry,
  isPreview = false,
}) => {
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const isDark = theme.palette.mode === "dark";
  const bg = theme.palette.background.default;
  const paper = theme.palette.background.paper;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const divider = theme.palette.divider;

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: isDark
          ? `linear-gradient(180deg, ${alpha(bg, 0.98)} 0%, ${alpha(paper, 0.6)} 100%)`
          : `linear-gradient(180deg, ${alpha(paper, 0.95)} 0%, ${alpha(bg, 0.9)} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 520,
          width: "100%",
          textAlign: "center",
          p: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(divider, 0.8)}`,
          backgroundColor: alpha(paper, isDark ? 0.4 : 0.85),
          boxShadow: `0 8px 32px ${alpha(
            isDark ? theme.palette.common.black : BRAND,
            0.08
          )}`,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            mx: "auto",
            mb: 2,
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: "2rem",
          }}
        >
          !
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: textPrimary,
            mb: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {isPreview ? "Error boundary preview" : "Something went wrong"}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: textSecondary,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          {isPreview
            ? "This is how the error page will look when an error is caught. Use the buttons below to refresh or go home."
            : "We're sorry, but something unexpected happened. Please try refreshing the page or return to the home page."}
        </Typography>

        {!isPreview && (errorMessage || componentStack) && (
          <Box sx={{ mb: 3, textAlign: "left" }}>
            <Button
              size="small"
              onClick={() => setShowDetails(!showDetails)}
              sx={{
                color: BRAND,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: alpha(BRAND, 0.08),
                },
              }}
            >
              {showDetails ? "Hide details" : "Show error details"}
            </Button>
            <Collapse in={showDetails}>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: alpha(
                    theme.palette.error.main,
                    isDark ? 0.12 : 0.06
                  ),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  overflow: "auto",
                  maxHeight: 200,
                }}
              >
                {errorMessage && (
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{
                      color: theme.palette.error.dark,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "monospace",
                    }}
                  >
                    {errorMessage}
                  </Typography>
                )}
                {componentStack && (
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{
                      color: textSecondary,
                      mt: 1,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "monospace",
                      fontSize: "0.7rem",
                    }}
                  >
                    {componentStack}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={onRetry ?? (() => window.location.reload())}
            sx={{
              background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
              color: "#fff",
              fontWeight: 600,
              px: 3,
              py: 1.25,
              boxShadow: `0 4px 14px ${alpha(BRAND, 0.4)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${BRAND} 0%, #e04119 100%)`,
                boxShadow: `0 6px 20px ${alpha(BRAND, 0.5)}`,
              },
            }}
          >
            Refresh page
          </Button>
          <Button
            variant="outlined"
            onClick={handleGoHome}
            sx={{
              borderColor: alpha(BRAND, 0.5),
              color: BRAND,
              fontWeight: 600,
              px: 3,
              py: 1.25,
              "&:hover": {
                borderColor: BRAND,
                backgroundColor: alpha(BRAND, 0.08),
              },
            }}
          >
            Go to home
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
  componentStack: string;
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: "",
      componentStack: "",
    };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      errorMessage: error?.toString?.() ?? String(error),
      componentStack: errorInfo?.componentStack ?? "",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          errorMessage={this.state.errorMessage}
          componentStack={this.state.componentStack}
          onRetry={() => window.location.reload()}
        />
      );
    }
    return this.props.children;
  }
}
